export interface AITopic {
  topic: string;
  description: string;
  category: string;
  engagement_score: number;
}

export interface SuggestTopicsResponse {
  metadata: {
    model: string;
    cached: boolean;
    token_usage: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
    };
    response_time: number;
    cost_optimized: boolean;
    batch_size: number | null;
    tokens_per_record: number | null;
    batch_processing: boolean | null;
  };
  topics: {
    topics: AITopic[];
    summary: string;
  };
}

export interface ContentByDate {
  date: string;
  text: string;
  summary: string;
  description: string;
  dayOfWeek: string;
  contentVariation: "short" | "medium" | "long";
  contentType: "social" | "email" | "blog";
  language: string;
  topic: string;
  customPrompt: string;
  notes: string | null;
  weekend: boolean;
}

/** One item from API result.result.contentList (StructuredDateContent). */
export interface StructuredDateContentItem {
  date: string;
  summary: string;
  description: string;
  dayOfWeek: string;
  weekend?: boolean;
  contentVariation?: string;
  contentType?: string;
  language?: string;
  topic?: string | null;
  customPrompt?: string | null;
  notes?: string | null;
}

/** Response from /generate-contents (StructuredDateContentLlmResponse). */
export interface GenerateContentsResponse {
  result: {
    contentList: StructuredDateContentItem[];
    notes?: string | null;
  };
  promptTokens: number;
  completionTokens: number;
  usedFallback: boolean;
}

/** Build contentByDate record from new API result for backward-compat usage in UI. */
export function contentListToContentByDate(
  contentList: StructuredDateContentItem[] | null | undefined
): Record<string, ContentByDate> {
  if (!Array.isArray(contentList)) return {};
  const out: Record<string, ContentByDate> = {};
  for (const c of contentList) {
    out[c.date] = {
      date: c.date,
      text: c.summary ?? "",
      summary: c.summary ?? "",
      description: c.description ?? "",
      dayOfWeek: c.dayOfWeek ?? "",
      contentVariation: (c.contentVariation as ContentByDate["contentVariation"]) ?? "medium",
      contentType: (c.contentType as ContentByDate["contentType"]) ?? "social",
      language: c.language ?? "",
      topic: c.topic ?? "",
      customPrompt: c.customPrompt ?? "",
      notes: c.notes ?? null,
      weekend: c.weekend ?? false,
    };
  }
  return out;
}

export interface SuggestTopicsRequest {
  projectId: string;
  service_description: string;
  customer_location: string;
  company_industry: string;
  language: string;
}

export interface GenerateContentsRequest {
  projectId: string;
  topic: string;
  prompt: string | null;
  start_date: string;
  end_date: string;
  max_days: number;
  include_weekends: boolean;
  language: string;
}

export interface AIContent {
  id: string;
  title: string;
  content: string;
  type: "email" | "proposal" | "report" | "presentation";
  tone: "professional" | "friendly" | "formal" | "casual";
  recipient?: string;
  sender?: string;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
  status: "draft" | "published" | "archived";
}

export interface ContentTemplate {
  id: string;
  name: string;
  description: string;
  type: "email" | "proposal" | "report" | "presentation";
  prompt: string;
  variables: string[];
  createdAt: string;
  updatedAt: string;
}

export interface SocialPost {
  id: string;
  projectId: string;
  type: string;
  data: {
    summary: { value: string; label: string; type: string };
    description: { value: string; label: string; type: string };
    platform: { value: string; label: string; type: string };
    status: { value: string; label: string; type: string };
    topic: { value: string; label: string; type: string };
    start: { value: string; label: string; type: string };
    startTime?: { value: string; label: string; type: string };
    scheduledTime?: { value: string; label: string; type: string };
    language?: { value: string; label: string; type: string };
    contentVariation?: { value: string; label: string; type: string };
    dayOfWeek?: { value: string; label: string; type: string };
    weekend?: { value: boolean; label: string; type: string };
    published?: { value: boolean; label: string; type: string };
    publishedBy?: { value: string; label: string; type: string };
    publishedDatetime?: { value: string; label: string; type: string };
    assignees?: { value: string[]; label: string; type: string };
    labels?: { value: string[]; label: string; type: string };
    organizer?: { value: string; label: string; type: string };
  };
  tenantId: string;
  version: number;
  iconUrl: string;
  iconType: string;
}

export interface GenerateSocialPostsResponse {
  metadata: {
    model: string;
    cached: boolean;
    token_usage: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
    };
    response_time: number;
    cost_optimized: boolean;
    batch_size: number | null;
    tokens_per_record: number | null;
    batch_processing: boolean | null;
  };
  socialPosts: SocialPost[];
}
